import { useEffect, useMemo, useState } from 'react'
import { Button, Col, Row } from 'react-bootstrap'
import CardCustom from '../../../../components/custom/card-custom'
import AutocompleteCustomA from '../../../../components/custom/autocompletecustom'
import InputSearch from '../../../../components/custom/InputSearch'
import { useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { updateRole } from '../../../../actions/role-org.action'
import { Pagination } from 'antd'
import RowPerPageCustom from '../../../../components/custom/RowPerPageCustom'
import GenericTable from '../../../../components/table'
import { TbListSearch } from 'react-icons/tb'
import {
  createSearchParams,
  useNavigate,
  useOutletContext
} from 'react-router-dom'
import { getLeveTypeV2API } from '../../../../services/setting/leave'
import {
  PAGE_SETTING_LEAVE_ADD,
  PAGE_SETTING_LEAVE_ASSIGN
} from '../../../../router/routes'
import { RxCopy } from 'react-icons/rx'
import { fnDateEn } from '../../../../helper/fnDateEn'
import TagOrgList from '../../../../components/custom/TagOrgListCustom'
import { TbSortDescending, TbSortAscending } from 'react-icons/tb'
import BadgeStatus from '../../../../components/BadgeStatus'

export default function SettingLeaveOrgPage() {
  const { orgRole } = useOutletContext()
  const { control, setValue } = useForm()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [details, setDetails] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const roleId = useSelector((state) => state.roleReducer.role)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [search, setSearch] = useState()
  const [totalRows, setTotalRows] = useState(0)
  const [isAssignBtnHover, setIsAssignBtnHover] = useState(false)
  const [sortOrder, setSortOrder] = useState(null)
  const offset = useMemo(() => (page - 1) * limit, [page, limit])

  useEffect(() => {
    if (!roleId) return
    const timer = setTimeout(() => {
      getIndexLeave({
        offset,
        limit,
        orgId: roleId,
        onlyAssign: true,
        search: search || ''
      })
    }, 400)

    return () => clearTimeout(timer)
  }, [roleId, search, limit])

  // Sync selected org from redux
  useEffect(() => {
    if (!roleId || !orgRole.length) return
    const org = orgRole.find((item) => String(item.org_id) === String(roleId))
    if (org) {
      setValue('selectedOrg', {
        label: org.org,
        value: org.org_id
      })
    }
  }, [roleId, orgRole, setValue])

  const getIndexLeave = async (body) => {
    setIsLoading(true)
    const res = await getLeveTypeV2API(body)
    setIsLoading(false)
    if (!res) return
    if (res.status) {
      setDetails(res.result)
      setTotalRows(res.total)
    } else {
      setDetails([])
      setTotalRows(0)
    }
  }

  const clearSearch = () => {
    setSearch('')
    setPage(1)

    getIndexLeave({
      onlyAssign: true,
      offset: 0,
      limit,
      orgId: Number(roleId),
      search: ''
    })
  }

  const handleChangeOrg = (option) => {
    if (!option) return

    const orgId = option.value
    dispatch(updateRole(orgId))

    setPage(1)
    setSearch('')

    getIndexLeave({
      onlyAssign: true,
      offset,
      limit,
      orgId,
      search: ''
    })
  }

  const handleSort = () => {
    setSortOrder((prev) => {
      if (prev === null) return 'asc'
      if (prev === 'asc') return 'desc'
      return null
    })
  }

  const sortedDetails = useMemo(() => {
    if (!sortOrder) return details

    const sorted = [...details].sort((a, b) => {
      const nameA = (a.leaveTypeEn || '').toLowerCase()
      const nameB = (b.leaveTypeEn || '').toLowerCase()

      if (sortOrder === 'asc') {
        return nameA.localeCompare(nameB)
      }
      return nameB.localeCompare(nameA)
    })

    return sorted
  }, [details, sortOrder])

  const columns = [
    {
      header: (
        <div
          onClick={handleSort}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            userSelect: 'none'
          }}
        >
          <span>{t('hrms_setting_leave_name_header')}</span>
          {sortOrder === null && (
            <TbSortDescending
              size={14}
              style={{
                color: 'var(--color-on-base-content-subtle)',
                opacity: 0.3
              }}
            />
          )}
          {sortOrder === 'asc' && (
            <TbSortDescending
              size={14}
              style={{ color: 'var(--color-on-base-content-subtle)' }}
            />
          )}
          {sortOrder === 'desc' && (
            <TbSortAscending
              size={14}
              style={{ color: 'var(--color-on-base-content-subtle)' }}
            />
          )}
        </div>
      ),
      minWidth: '284px',
      maxWidth: '284px',
      headerStyle: {
        textAlign: 'left',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap'
      }
    },
    {
      header: t('hrms_setting_leave_total_header'),
      className: 'text-center',
      minWidth: '80px',
      maxWidth: '80px',
      headerStyle: {
        textAlign: 'center',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap'
      }
    },
    {
      header: t('hrms_setting_leave_emp_type_header'),
      className: 'text-center',
      minWidth: '110px',
      maxWidth: '110px',
      headerStyle: {
        textAlign: 'center',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap'
      }
    },
    {
      header: t('hrms_setting_leave_effecttive-start-end_header'),
      className: 'text-center',
      minWidth: '180px',
      maxWidth: '180px',
      headerStyle: {
        textAlign: 'left',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap'
      }
    },
    {
      header: t('hrms_setting_leave_assign_to_header'),
      className: 'text-center',
      minWidth: '284px',
      maxWidth: '284px',
      headerStyle: {
        textAlign: 'left',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap'
      }
    },
    {
      header: t('hrms_setting_leave_status_header'),
      className: 'text-center',
      minWidth: '86px',
      maxWidth: '86px',
      headerStyle: {
        textAlign: 'center',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap'
      }
    },

    {
      header: t('hrms_setting_leave_action_header'),
      className: 'text-center',
      minWidth: '86px',
      maxWidth: '86px',
      headerStyle: {
        textAlign: 'center',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap'
      }
    }
  ]

  const formatDateRange = (start, end) => {
    if (!start && !end) return '-'

    const s = start ? fnDateEn(start).format('DD/MM/YYYY') : ''
    const e = end ? fnDateEn(end).format('DD/MM/YYYY') : ''

    return `${s}${s && e ? ' - ' : ''}${e}`
  }

  const renderLocationRow = (details, baseCell, onEdit, setIsExpanded) => {
    const nameCellStyle = (minWidth) => ({
      ...baseCell, // ใช้ baseCell แทน borderStyle
      minWidth,
      textAlign: 'left',
      whiteSpace: 'nowrap',
      padding: '16px 12px' // เพิ่ม horizontal padding ด้วย
    })

    const centerCellStyle = (minWidth) => ({
      ...baseCell, // ใช้ baseCell แทน borderStyle
      minWidth,
      textAlign: 'center',
      verticalAlign: 'top',
      whiteSpace: 'nowrap',
    })

    return (
      <>
        <td style={nameCellStyle('284px')}>
          <p
            className="mb-0 "
            style={{
              color: ' var(--color-on-base-content-high)'
            }}
          >
            <div className="d-flex gap-3 align-items-center">
              <div>
                <div style={{ fontSize: '14px', fontWeight: 400 }}>
                  {`${details.leaveType}` ?? '-'}
                </div>
              </div>
            </div>
          </p>
        </td>

        <td
          style={{
            ...nameCellStyle('80px'),
            maxWidth: '150px'
          }}
        >
          <p className="mb-0 font-small-2 text-center" style={{
            color: 'var(--color-on-base-content-high)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            {details.leaveTypeTotal ? `${details.leaveTypeTotal}` : '-'}
          </p>
        </td>
        <td style={nameCellStyle('110px')}>
          <p
            className="mb-0 font-small-2"
            style={{
              color: 'var(--color-on-base-content-high)',
              justifyContent: 'center',
              alignItems: 'center',
              display: 'flex'
            }}
          >
            {details?.empType?.typeName ? `${details.empType.typeName}` : '-'}
          </p>
        </td>

        <td style={nameCellStyle('180px')}>
          <p
            className="mb-0 font-small-2"
            style={{
              color: 'var(--color-on-base-content-high)'
            }}
          >
            {formatDateRange(details?.effectiveStart, details?.effectiveEnd)}
          </p>
        </td>

        <td style={nameCellStyle('284px')}>
          <p className="mb-0 font-small-2">
            {details?.orgAssign?.length > 0 ? (
              <TagOrgList orgAssign={details?.orgAssign} onExpand={() => setIsExpanded?.(true)} />
            ) : (
              '-'
            )}
          </p>
        </td>
        <td style={nameCellStyle('86px')}>
          <BadgeStatus
            isActive={details?.leaveTypeStatus}
            activeText={t('hrms_setting_leave_active_status')}
            inactiveText={t('hrms_setting_leave_inactive_status')}
          />
        </td>

        <td style={nameCellStyle('86px')}>
          <div className="d-flex justify-content-center w-100">
            <Button
              variant="light"
              className="text-primary rounded-2 px-2"
              style={{
                width: '40px',
                height: '32px',
                borderRadius: '8px',
                borderColor: 'var(--color-on-base-border-default)',
                backgroundColor: 'var(--color-base-foreground-elevated-16)'
              }}
              size="sm"
              onClick={() => onEdit?.(details?.leaveTypeId ?? '')}
            >
              <TbListSearch
                size={18}
                style={{
                  color: 'var(--color-on-base-content-high)'
                }}
              />
            </Button>
          </div>
        </td>
      </>
    )
  }
  const navigate = useNavigate()

  const handleEditLeave = (d) => {
    navigate({
      pathname: PAGE_SETTING_LEAVE_ADD,
      search: `?${createSearchParams({
        id: d ?? '',
        page: 'org'
      })}`
    })
  }
  const handleAssign = () => {
    navigate({
      pathname: PAGE_SETTING_LEAVE_ASSIGN
    })
  }
  return (
    <Row>
      <Col className="">
        <CardCustom style={{ minHeight: '' }}>
          <Row className="p-3 g-3 flex-shrink-0">
            <Col xl={2} md={4}>
              <Controller
                name="selectedOrg"
                control={control}
                render={({ field }) => (
                  <AutocompleteCustomA
                    isClearable={false}
                    value={field.value}
                    options={orgRole.map((org) => ({
                      label: org.org,
                      value: org.org_id
                    }))}
                    onChange={(option) => {
                      field.onChange(option) // ✅ ให้ react-hook-form รับค่า
                      handleChangeOrg(option) // ✅ ทำ logic เพิ่มของคุณ
                    }}
                  />
                )}
              />
            </Col>

            <Col xl={4} md={6}>
              <InputSearch
                value={search}
                onChange={(val) => {
                  if (!val) {
                    clearSearch()
                  } else {
                    setSearch(val)
                    setPage(1)
                  }
                }}
                placeholder={t('hrms_setting_leave_search')}
              />
            </Col>

            <Col xl={6} md={2} className="text-end">
              <Button
                onClick={handleAssign}
                onMouseEnter={() => setIsAssignBtnHover(true)}
                onMouseLeave={() => setIsAssignBtnHover(false)}
                style={{
                  height: 40,
                  padding: '0 16px',
                  borderColor: 'var(--color-primary-border-default)',
                  backgroundColor: isAssignBtnHover
                    ? '#dee7fb'
                    : 'var(--comp-btn-color-background-secondary-normal)',
                  color: 'var(--color-primary-content-default)',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  cursor: 'pointer'
                }}
              >
                <RxCopy size={16} /> {t('hrms_setting_leave_assign_bt')}
              </Button>
            </Col>
          </Row>

          {/* ===== Table ===== */}

          {/* <div
            style={{
              flex: 1,
              minHeight: 0, 
              overflowY: 'auto',
              position: 'relative',
            }}
          > */}
          <GenericTable
            data={sortedDetails}
            columns={columns}
            isLoading={isLoading}
            rowStartIndex={(page - 1) * limit}
            renderRow={(item, index, baseCell, activeTab, setIsExpanded) =>
              renderLocationRow(item, baseCell, handleEditLeave, setIsExpanded)
            }
            visible_rows={10}
          />
          {/* </div> */}

          {/* <div
            style={{
              flexShrink: 0,
              borderTop: '1px solid var(--color-base-border-default)',
              padding: '0px 12px',
              boxShadow:
                '0px 0px 1px 0px #0000001F, 0px 4px 16px -4px #00000014',
            }}
          > */}
          <Row
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              margin: 16
            }}
          >
            <Col xl={6} md={12}>
              <RowPerPageCustom
                onChange={(d) => {
                  setPage(1)
                  setLimit(d)
                  getIndexLeave({
                    offset: 0,
                    limit: d,
                    orgId: Number(roleId),
                    search: search || '',
                    onlyAssign: true
                  })
                }}
                currentSize={limit}
              />
            </Col>
            <Col xl={6} md={12} className="d-flex justify-content-end">
              <Pagination
                size="small"
                current={page}
                pageSize={limit}
                total={totalRows}
                showSizeChanger={false}
                onChange={(p) => {
                  setPage(p)
                  getIndexLeave({
                    offset: (p - 1) * limit,
                    limit,
                    orgId: roleId,
                    search: search || '',
                    onlyAssign: true
                  })
                }}
              />
            </Col>
          </Row>
          {/* </div> */}
        </CardCustom>
      </Col>
    </Row>
  )
}
